import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const ShipmentDetailsPanel = ({ shipment, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('logistics');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(shipment || {});

  if (!shipment) {
    return (
      <div className="bg-card border-l border-border h-full flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <Icon name="Ship" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">Select a shipment to view details</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'logistics', label: 'Logistics', icon: 'Ship' },
    { id: 'pricing', label: 'Pricing', icon: 'DollarSign' },
    { id: 'documentation', label: 'Documentation', icon: 'FileText' },
    { id: 'issues', label: 'Issues', icon: 'AlertTriangle' }
  ];

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(shipment);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'contract-signed': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'lc-received': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      'loading': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      'in-transit': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      'discharged': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'completed': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    };
    return statusColors?.[status] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="bg-card border-l border-border h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="Ship" size={24} color="var(--color-primary)" />
              <h2 className="text-xl font-semibold text-foreground">{shipment?.vesselName}</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(shipment?.status)}`}>
                {shipment?.status?.replace('-', ' ')?.toUpperCase()}
              </span>
              <span className="text-sm text-muted-foreground">•</span>
              <span className="text-sm text-muted-foreground font-data">{shipment?.contractRef}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent/10 rounded transition-colors"
            aria-label="Close details panel"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent/10 hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
        {activeTab === 'logistics' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Logistics Information</h3>
              {!isEditing ? (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit2"
                  iconPosition="left"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Save"
                    iconPosition="left"
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Supplier</label>
                <p className="text-sm text-muted-foreground">{shipment?.supplier}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Buyer</label>
                <p className="text-sm text-muted-foreground">{shipment?.buyer}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Port</label>
                <p className="text-sm text-muted-foreground">{shipment?.port}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Incoterm</label>
                <p className="text-sm text-muted-foreground">{shipment?.incoterm}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tonnage</label>
                <p className="text-sm text-muted-foreground font-data">{shipment?.tonnage?.toLocaleString()} MT</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Coal Grade</label>
                <p className="text-sm text-muted-foreground">{shipment?.coalGrade}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Laycan From</label>
                <p className="text-sm text-muted-foreground">{formatDate(shipment?.laycanFrom)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Laycan To</label>
                <p className="text-sm text-muted-foreground">{formatDate(shipment?.laycanTo)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">ETD</label>
                <p className="text-sm text-muted-foreground">{formatDate(shipment?.etd)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">ETA</label>
                <p className="text-sm text-muted-foreground">{formatDate(shipment?.eta)}</p>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3">Vessel Specifications</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Vessel Type</label>
                  <p className="text-sm text-muted-foreground">Capesize</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">DWT</label>
                  <p className="text-sm text-muted-foreground font-data">180,000 MT</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Flag</label>
                  <p className="text-sm text-muted-foreground">Panama</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Built Year</label>
                  <p className="text-sm text-muted-foreground">2018</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pricing' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Pricing Details</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Base Price</label>
                <p className="text-sm text-muted-foreground font-data">${shipment?.basePrice}/MT</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Discount</label>
                <p className="text-sm text-muted-foreground font-data">{shipment?.discount}%</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Pricing Window</label>
                <p className="text-sm text-muted-foreground">{shipment?.pricingWindow}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Platts Index</label>
                <p className="text-sm text-muted-foreground">{shipment?.plattsIndex}</p>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-semibold text-foreground mb-3">Financial Summary</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Contract Value</span>
                  <span className="text-sm font-semibold text-foreground font-data">
                    ${(shipment?.tonnage * shipment?.basePrice)?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Discount Amount</span>
                  <span className="text-sm font-semibold text-foreground font-data">
                    -${((shipment?.tonnage * shipment?.basePrice * shipment?.discount) / 100)?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-border">
                  <span className="text-sm font-semibold text-foreground">Net Value</span>
                  <span className="text-lg font-bold text-primary font-data">
                    ${((shipment?.tonnage * shipment?.basePrice) * (1 - shipment?.discount / 100))?.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documentation' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Documents</h3>
              <Button
                variant="outline"
                size="sm"
                iconName="Upload"
                iconPosition="left"
              >
                Upload
              </Button>
            </div>

            <div className="space-y-3">
              {[
                { name: 'Contract Agreement.pdf', size: '2.4 MB', date: '2025-11-15', type: 'Contract' },
                { name: 'LC Document.pdf', size: '1.8 MB', date: '2025-11-20', type: 'LC' },
                { name: 'B/L Copy.pdf', size: '3.2 MB', date: '2025-12-01', type: 'B/L' },
                { name: 'Inspection Report.pdf', size: '4.1 MB', date: '2025-12-05', type: 'Inspection' },
                { name: 'Quality Certificate.pdf', size: '1.5 MB', date: '2025-12-05', type: 'Quality' }
              ]?.map((doc, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded">
                      <Icon name="FileText" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{doc?.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <span>{doc?.size}</span>
                        <span>•</span>
                        <span>{doc?.type}</span>
                        <span>•</span>
                        <span>{formatDate(doc?.date)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="p-1.5 hover:bg-accent/10 rounded transition-colors" title="Download">
                      <Icon name="Download" size={16} color="var(--color-muted-foreground)" />
                    </button>
                    <button className="p-1.5 hover:bg-accent/10 rounded transition-colors" title="View">
                      <Icon name="Eye" size={16} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'issues' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Related Issues</h3>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                iconPosition="left"
              >
                Log Issue
              </Button>
            </div>

            <div className="space-y-3">
              {[
                {
                  id: 'ISS-2025-001',
                  title: 'Delay in LC receipt from buyer',
                  severity: 'high',
                  status: 'open',
                  date: '2025-11-18',
                  owner: 'Finance Team'
                },
                {
                  id: 'ISS-2025-002',
                  title: 'Quality specification clarification needed',
                  severity: 'medium',
                  status: 'in-progress',
                  date: '2025-11-22',
                  owner: 'Operations Team'
                }
              ]?.map((issue, index) => (
                <div
                  key={index}
                  className="p-4 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-data text-muted-foreground">{issue?.id}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          issue?.severity === 'high' ?'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}
                      >
                        {issue?.severity?.toUpperCase()}
                      </span>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        issue?.status === 'open' ?'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                      }`}
                    >
                      {issue?.status?.replace('-', ' ')?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground mb-2">{issue?.title}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      <span>{issue?.owner}</span>
                    </div>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} />
                      <span>{formatDate(issue?.date)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipmentDetailsPanel;